import {
  CampaignAuditColumns,
  CampaignStatus,
  ClientDeviceNames,
  DeviceCaps,
  ViewId
} from "@madhive/mad-sdk";
import { LineItemActionRequiredInstructionStatus } from "lib/constants/campaign";

export enum LineItemQueryParams {
  /** The members of this enum will be used as the value of the query string in URLs. */
  ATTACHING_CREATIVE = "attaching_creative",
  TAB = "tab",
  VIEW_MODE = "mode"
}

export enum LineItemTab {
  OVERVIEW = "overview",
  PRODUCT_AND_INVENTORY = "product-and-inventory",
  TARGETING = "targeting",
  CREATIVES = "creatives"
}

export enum TargetingSubTab {
  GEOGRAPHIC_SELECTION = "geographic-select",
  AUDIENCE_SEGMENT = "audience-segment",
  INVENTORY = "inventory",
  DEVICE_TYPE = "device-type"
}

export enum LineItemViewMode {
  CREATE = "create",
  EDIT = "edit",
  READONLY = "readonly"
}

export const ACTION_REQUIRED_STATUS_TO_LINE_ITEM_TABS: Readonly<
  Record<
    LineItemActionRequiredInstructionStatus,
    {
      tab: LineItemTab;
      subTab: TargetingSubTab | null;
    }
  >
> = {
  [CampaignStatus.MISSING_CREATIVE]: {
    tab: LineItemTab.CREATIVES,
    subTab: null
  },
  [CampaignStatus.MISSING_WHITELIST]: {
    tab: LineItemTab.TARGETING,
    subTab: TargetingSubTab.INVENTORY
  },
  [CampaignStatus.MISSING_GEO]: {
    tab: LineItemTab.TARGETING,
    subTab: null
  },
  [CampaignStatus.MISSING_GOAL]: {
    tab: LineItemTab.OVERVIEW,
    subTab: null
  },
  [CampaignStatus.MISSING_PRODUCT]: {
    tab: LineItemTab.TARGETING,
    subTab: null
  },
  [CampaignStatus.MISSING_FREQ_CAP]: {
    tab: LineItemTab.TARGETING,
    subTab: null
  }
};

export const MAX_DAILY_BUDGET = 50000;
export const MAX_LIFETIME_BUDGET = 250000;

export const DEFAULT_DEVICE_CAPS: DeviceCaps = {
  [ClientDeviceNames.CTV]: 100,
  [ClientDeviceNames.MOBILE]: 0,
  [ClientDeviceNames.DESKTOP]: 0,
  [ClientDeviceNames.TABLET]: 0
};

export const DSP_OPTIONS = [
  { id: "0LDY6rsTok3wcPtS29QFr97Mtpw7", label: "Madhive" },
  { id: "NaYohTJvHtJVMSHZW0CIhepYt5Xi", label: "Google Ad Manager ( GAM )" },
  { id: "3SLsQgGodlAmVrUmZYoQ1GcZoMDj", label: "Xandr" }
];

export const TPA_ERROR = "TPA will be generated on save";

export const CREATE_LINE_ITEM_KEY = "CREATE_LINE_ITEM_KEY";

export const LINEITEM_AUDIT_COLUMN_HEADERS: CampaignAuditColumns[] = [
  CampaignAuditColumns.NAME,
  CampaignAuditColumns.ID,
  CampaignAuditColumns.VERSION,
  CampaignAuditColumns.UPDATED_BY,
  CampaignAuditColumns.UPDATED,
  CampaignAuditColumns.START_DATE,
  CampaignAuditColumns.END_DATE,
  CampaignAuditColumns.IMPRESSIONS,
  CampaignAuditColumns.DAYPARTS,
  CampaignAuditColumns.DEVICE_CAPS,
  CampaignAuditColumns.PRODUCT_ID,
  CampaignAuditColumns.PUB_GROUP_ID,
  CampaignAuditColumns.GEO_INCLUDE,
  CampaignAuditColumns.GEO_EXCLUDE,
  CampaignAuditColumns.FREQUENCIES,
  CampaignAuditColumns.SEGMENTS,
  CampaignAuditColumns.CREATIVES,
  CampaignAuditColumns.EXT_METAS,
  CampaignAuditColumns.EXTERNAL_ID,
  CampaignAuditColumns.STATUS,
  CampaignAuditColumns.CATEGORIES
];

export const LINE_ITEM_RESTRICTED_VIEW_DIALOG = {
  [ViewId.CREATIVE_LIBRARY]:
    "Unable to upload and attach creatives to creative scheduling. Please modify creative library view permissions for user.",
  [ViewId.MANAGE_CAMPAIGNS]: "Unable to manage campaigns."
} as const;

export const LINE_ITEM_TAB_DISPLAY_NAMES: Record<LineItemTab, string> = {
  [LineItemTab.OVERVIEW]: "Overview",
  [LineItemTab.PRODUCT_AND_INVENTORY]: "Product & Inventory",
  [LineItemTab.TARGETING]: "Targeting",
  [LineItemTab.CREATIVES]: "Creative Scheduling"
};
